import { z } from 'zod';

/**
 * Backup DTOs — two different things that share one word, kept apart on
 * purpose:
 *
 *  - an OLT CONFIG backup is a chassis's running config. Those already
 *    existed; what is new is that the body lives on the file store instead of
 *    in a text column, so a row is metadata plus a storage key.
 *  - a PLATFORM backup is this installation's own disaster-recovery copy: a
 *    consistent snapshot of the database, plus what is not in the database,
 *    taken to disk and mirrored off-box.
 *
 * Neither one restores a chassis. Pushing config back to an OLT is its own
 * surface (`/olts/:id/restore`) and is deliberately not reachable from here.
 */

/** How a platform backup ended. `running` is the row a crash leaves behind. */
export const platformBackupStatusSchema = z.enum(['running', 'verified', 'failed']);
export type PlatformBackupStatus = z.infer<typeof platformBackupStatusSchema>;

export const platformBackupKindSchema = z.enum(['scheduled', 'manual']);
export type PlatformBackupKind = z.infer<typeof platformBackupKindSchema>;

/**
 * What happened to `.env`.
 *
 * The database is encrypted with `APP_SECRET_KEY`, which lives in `.env`.
 * Putting both in one bucket means one compromised bucket yields plaintext,
 * so the default is `exclude` and the only built-in alternative keeps `.env`
 * on this server and never mirrors it. A second, separately-credentialled
 * remote destination is an operator decision, not a default.
 */
export const envBackupModeSchema = z.enum(['exclude', 'local-only']);
export type EnvBackupMode = z.infer<typeof envBackupModeSchema>;

export const platformBackupSchema = z.object({
  id: z.string(),
  kind: platformBackupKindSchema,
  status: platformBackupStatusSchema,
  startedAt: z.number().int(),
  finishedAt: z.number().int().nullable(),
  /** Bytes of the database snapshot itself. */
  dbBytes: z.number().int(),
  /** Bytes of everything else in the set (the local file store, when included). */
  filesBytes: z.number().int(),
  fileCount: z.number().int(),
  /** sha256 of the database snapshot — what verification compares against. */
  sha256: z.string().nullable(),
  /** True only once the snapshot passed `PRAGMA integrity_check` and read back. */
  verified: z.boolean(),
  verifyDetail: z.string().nullable(),
  /** Where the set sits on this server. */
  localPath: z.string().nullable(),
  /** Prefix in the file store, when the set was mirrored off-box. */
  storageKey: z.string().nullable(),
  mirroredAt: z.number().int().nullable(),
  mirrorError: z.string().nullable(),
  /** Whether the local file store was captured into the set. */
  includesFiles: z.boolean(),
  envMode: envBackupModeSchema,
  error: z.string().nullable(),
  createdBy: z.string().nullable(),
});
export type PlatformBackup = z.infer<typeof platformBackupSchema>;

export const platformBackupSettingsSchema = z.object({
  enabled: z.boolean(),
  /** Server-local hour the scheduled backup may start after (0-23). */
  hour: z.number().int().min(0).max(23),
  /** How many scheduled backups to keep. The newest verified one is never pruned. */
  keep: z.number().int().min(2).max(60),
  /** Directory on this server the sets are written to. */
  localDir: z.string(),
  /** Capture the local file store when the store is a directory on this box. */
  includeFiles: z.boolean(),
  envMode: envBackupModeSchema,
});
export type PlatformBackupSettings = z.infer<typeof platformBackupSettingsSchema>;

export const updatePlatformBackupSettingsSchema = platformBackupSettingsSchema
  .partial()
  .omit({ localDir: true })
  .extend({ localDir: z.string().max(300).optional() });
export type UpdatePlatformBackupSettings = z.infer<typeof updatePlatformBackupSettingsSchema>;

/**
 * The answer to "is anything silently not running?" — the question a backup
 * system exists to answer and the one it usually fails at.
 */
export const backupHealthSchema = z.object({
  platform: z.object({
    lastSuccessAt: z.number().int().nullable(),
    lastAttemptAt: z.number().int().nullable(),
    ageHours: z.number().nullable(),
    /** No verified backup inside the expected window. */
    stale: z.boolean(),
    /** The most recent verified set was mirrored off this server. */
    offBox: z.boolean(),
    lastError: z.string().nullable(),
    verifiedCount: z.number().int(),
  }),
  oltConfigs: z.object({
    total: z.number().int(),
    /** Rows whose body is on the file store rather than in the database. */
    onStore: z.number().int(),
    /** Rows still holding their body in the `content` column. */
    inDatabase: z.number().int(),
    /** Bytes of config text still sitting in the database. */
    databaseBytes: z.number().int(),
    /** OLTs whose newest scheduled backup is older than the staleness window. */
    staleOlts: z.array(z.object({ id: z.string(), name: z.string(), lastAt: z.number().int().nullable() })),
  }),
  store: z.object({
    driver: z.string(),
    /** False when the file store is a directory on this same server. */
    offBox: z.boolean(),
  }),
});
export type BackupHealth = z.infer<typeof backupHealthSchema>;

export const platformBackupsResponseSchema = z.object({
  items: z.array(platformBackupSchema),
  settings: platformBackupSettingsSchema,
  health: backupHealthSchema,
});
export type PlatformBackupsResponse = z.infer<typeof platformBackupsResponseSchema>;

/** Result of moving OLT config bodies out of the database and onto the store. */
export const oltBodyCarryResultSchema = z.object({
  dryRun: z.boolean(),
  /** Rows whose body is still in the database at the start of the run. */
  pending: z.number().int(),
  carried: z.number().int(),
  /** Rows that failed verification — their `content` is untouched. */
  failed: z.number().int(),
  bytes: z.number().int(),
  errors: z.array(z.object({ backupId: z.string(), error: z.string() })),
});
export type OltBodyCarryResult = z.infer<typeof oltBodyCarryResultSchema>;

export const oltBodyDropResultSchema = z.object({
  /** Rows whose stored copy was re-verified byte-for-byte before clearing. */
  cleared: z.number().int(),
  /** Rows left alone because the stored copy did not match. */
  refused: z.number().int(),
  bytesFreed: z.number().int(),
  errors: z.array(z.object({ backupId: z.string(), error: z.string() })),
});
export type OltBodyDropResult = z.infer<typeof oltBodyDropResultSchema>;
